import posthog, { type CaptureResult, type Properties } from 'posthog-js';

const projectToken = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN;
const apiHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

export const isPostHogConfigured = Boolean(projectToken && apiHost);

/**
 * Staff who hold a merchant or processor account for testing. Their events are sent and
 * tagged `is_internal`, then dropped at query time by the project's "Filter out internal
 * and test users" setting - so QA can still verify tracking end to end.
 */
export const INTERNAL_EMAIL_DOMAINS = ['designkey.us', 'designkey.studio', 'payblox.com'];

/** Staff-only surfaces. These send nothing at all. */
const STAFF_ROLES = ['admin', 'manager'];

export interface AnalyticsUser {
  userId?: number;
  email?: string;
  role?: string;
  /** Set by useAuthUser when this session is impersonating another account. */
  logout?: boolean;
}

/**
 * True when nothing may be sent at all: the admin and manager panels, and impersonated
 * sessions. PostHog has no server-side equivalent - its internal-user filter runs at
 * query time, so the events would still be ingested.
 */
export function isExcludedFromCapture(user?: AnalyticsUser): boolean {
  if (!user) return false;
  // An impersonated session carries the merchant's own userId, so it would otherwise
  // be recorded as that merchant's activity.
  if (user.logout) return true;

  return Boolean(user.role && STAFF_ROLES.includes(user.role));
}

/** Sent as a person property so PostHog can filter these out of insights. */
export function isInternalEmail(email?: string): boolean {
  const domain = email?.split('@')[1]?.toLowerCase();
  return Boolean(domain && INTERNAL_EMAIL_DOMAINS.includes(domain));
}

// PostHog fills $current_url, $session_entry_url, $referrer and friends from
// window.location, and /merchant/sign-in is reached with the prospect's email, phone
// and name in the query string.
const URL_PROPERTY_PATTERN = /(url|referrer)$/i;

const stripQuery = (value: unknown): unknown =>
  typeof value === 'string' ? value.split('#')[0].split('?')[0] : value;

const stripUrlProperties = (properties: Properties): Properties => {
  const sanitized: Properties = {};

  Object.entries(properties).forEach(([key, value]) => {
    if (URL_PROPERTY_PATTERN.test(key)) {
      sanitized[key] = stripQuery(value);
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      sanitized[key] = stripUrlProperties(value as Properties);
    } else {
      sanitized[key] = value;
    }
  });

  return sanitized;
};

const sanitizeEvent = (event: CaptureResult | null): CaptureResult | null => {
  if (!event) return null;

  return {
    ...event,
    // $ip: null stops PostHog storing the client IP.
    properties: { ...stripUrlProperties(event.properties ?? {}), $ip: null },
    ...(event.$set ? { $set: stripUrlProperties(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: stripUrlProperties(event.$set_once) } : {}),
  };
};

export function initializePostHog(): boolean {
  if (isPostHogConfigured) {
    posthog.init(projectToken, {
      api_host: apiHost,
      defaults: '2026-01-30',
      // Autocapture records the text of clicked elements, which leaks merchant names.
      autocapture: false,
      // Captured from the router instead, so nothing is sent before we know the role.
      capture_pageview: false,
      disable_session_recording: true,
      disable_surveys: true,
      person_profiles: 'identified_only',
      // No feature flags are used, and skipping the call means an opted-out browser
      // makes no request to PostHog at all.
      advanced_disable_decide: true,
      before_send: sanitizeEvent,
      loaded: (loadedPosthog) => {
        // MODE, not VITE_APP_ENVIRONMENT - that one resolves to 'local' in a prod build.
        loadedPosthog.register({ environment: import.meta.env.MODE });
      },
    });
    return true;
  }

  if (import.meta.env.DEV) {
    const missingVariable = projectToken
      ? 'VITE_PUBLIC_POSTHOG_HOST'
      : 'VITE_PUBLIC_POSTHOG_PROJECT_TOKEN';
    // Loud, but not thrown - a missing key must never stop the app booting.
    console.error(
      new Error(
        `${missingVariable} variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once ${missingVariable} is configured`,
      ),
    );
  }

  return false;
}

const canCapture = () => isPostHogConfigured && !posthog.has_opted_out_capturing();

export function captureEvent(event: string, properties?: Properties): void {
  if (!canCapture()) return;
  posthog.capture(event, properties);
}

export function capturePageview(properties?: Properties): void {
  if (!canCapture()) return;
  posthog.capture('$pageview', properties);
}

export function captureError(error: unknown, properties?: Properties): void {
  if (!canCapture()) return;
  posthog.captureException(error, properties);
}

const SIGNUP_COMPLETED_STORAGE_PREFIX = 'ph_signup_completed:';

// Deduped per browser, so a returning merchant on a new device fires it again. PostHog
// funnels count unique persons, so the funnel is unaffected.
const captureSignupCompleted = (distinctId: string, role?: string): void => {
  const storageKey = `${SIGNUP_COMPLETED_STORAGE_PREFIX}${distinctId}`;
  const completedAt = new Date().toISOString();

  try {
    if (localStorage.getItem(storageKey)) return;
    localStorage.setItem(storageKey, completedAt);
  } catch {
    // Storage unavailable - skip rather than re-fire on every load.
    return;
  }

  captureEvent('signup_completed', { role, $set_once: { signup_completed_at: completedAt } });
};

/**
 * Sends the account id, role and an is_internal flag - never the email itself, which is
 * only read in the browser to derive that flag. Staff-only surfaces are opted out
 * instead, which persists in localStorage so later loads short-circuit before PostHog
 * makes any request.
 */
export function identifyUser(user?: AnalyticsUser): void {
  if (!isPostHogConfigured) return;

  if (isExcludedFromCapture(user)) {
    if (!posthog.has_opted_out_capturing()) posthog.opt_out_capturing();
    return;
  }

  // The same browser may have been used by a staff account earlier.
  if (posthog.has_opted_out_capturing()) posthog.opt_in_capturing({ captureEventName: false });

  if (!user?.userId) return;

  const distinctId = String(user.userId);
  const identifiedUserId = posthog.get_property('$user_id');
  if (identifiedUserId && identifiedUserId !== distinctId) {
    posthog.reset();
  }

  posthog.identify(distinctId, {
    role: user.role,
    is_internal: isInternalEmail(user.email),
  });

  captureSignupCompleted(distinctId, user.role);
}

export function resetUser(role?: string): void {
  // reset() also clears consent, which would quietly opt a staff machine back in.
  if (!isPostHogConfigured || posthog.has_opted_out_capturing()) return;

  captureEvent('user_logged_out', { role });
  posthog.reset();
}

export default posthog;
